/* The progress rows are the tile's own — the widget's bordered bars are built for a full page, and
   the detail states the same numbers in the height an expanded tile has. */
import cn from 'classnames'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  ATTENTION_STATUS_REMIND_DBM_ACTION_KEY,
} from 'partner360/entities/attentionStatuses'
import {
  getCompletenessItems,
  type ICompletenessItem,
  p360TabsIds,
} from 'partner360/entities/partnerCompleteness'
import { useAcknowledgmentPendingSigners } from 'partner360/features/acknowledgmentStatusDialog'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { appendP360TabScrollIntent } from 'partner360/lib/tabScrollIntent'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getAcknowledgedOfferingData } from '../../../../lib/getAcknowledgedOfferingData'
import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import {
  selectCockpitCompletenessData,
  selectCockpitPartnerCompleteness,
  selectCockpitPartnerData,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionIntroFoot } from '../../hooks/useAttentionIntroSlot'
import { usePartnerPbmReminders } from '../../hooks/usePartnerPbmReminders'
import { usePlanSignFlow } from '../attentionNextSteps/usePlanSignFlow'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusCompleteness.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.completeness
const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const REMINDER_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.reminder
const NEXT_STEP_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps

/* The number is truncated rather than rounded: 99.8% is not a finished section, and a rounded
   `100%` beside an unfinished bar is the one reading the table must never allow. */
const toPercent = (value: number | undefined) => Math.floor((value ?? 0) * 100)

const REMIND_PBM_KEY = ATTENTION_STATUS_ACTIONS.remindPbm.labelKey
const ACKNOWLEDGE_KEY = ATTENTION_STATUS_ACTIONS.acknowledge.labelKey

/* With no sections measured, the table keeps its shape as empty rows rather than a sentence. */
const EMPTY_SECTION_ROWS = 6

/* The partner page scrolls to its Basic info widget and its contacts table, but from the cockpit
   both live on the Basic info tab, so the rows trade the anchor for that tab's address — it opens
   in its window over the cockpit and the detail stays open under it. */
const COCKPIT_BASIC_INFO_URL_PARAMS = { 'jbp-tab': '23' }

const COCKPIT_BASIC_INFO_TAB_ITEMS = new Set(['basic', 'contact'])

const toCockpitCompletenessItem = (item: ICompletenessItem): ICompletenessItem =>
  COCKPIT_BASIC_INFO_TAB_ITEMS.has(item.id)
    ? { ...item, scrollElementId: undefined, urlParams: COCKPIT_BASIC_INFO_URL_PARAMS }
    : item

interface IAttentionStatusCompletenessProps {
  /** Drawn in place of an empty attention list rather than as a status' detail: there is no status
   *  to act on, so the bar and the signing dialog it opens are left out, and a short section is not
   *  flagged red. */
  isOverview?: boolean
}

/**
 * The completeness detail of the `Offering X is not planned` status: how far the offering's plan
 * is, whether the PBM has signed it, and the percentage of every section the number is made of —
 * so the status is answered where it is raised.
 *
 * A plan is finished only when it is both measured full and acknowledged: 100% on its own still
 * leaves the status raised, so the signature is stated beside the total rather than left to the
 * neighbouring acknowledgment status.
 *
 * The numbers come from the cockpit's own payload rather than from the partner page's legacy
 * `completeness` slice: the cockpit fetches its data itself, and the page's request is not
 * guaranteed to have run beside it.
 */
export const AttentionStatusCompleteness = ({
  isOverview = false,
}: IAttentionStatusCompletenessProps) => {
  const t = useSafetyTranslation()
  const { isRemote, openSection, openUrl } = useCockpitSectionNavigation()
  const completenessProfiles = useSelector(selectCockpitPartnerCompleteness)
  const completenessData = useSelector(selectCockpitCompletenessData)
  const partnerData = useSelector(selectCockpitPartnerData)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const activeOffering = useAttentionDetailOffering()
  const completeness = useMemo(
    () => completenessProfiles.find(({ profile }) => profile === activeOffering),
    [completenessProfiles, activeOffering]
  )

  const completenessItems = useMemo(
    () => getCompletenessItems(completeness, isDistributor).map(toCockpitCompletenessItem),
    [completeness, isDistributor]
  )

  const totalPercentage = toPercent(completeness?.total)

  /* The header states how much of the table is finished, so the rows below are read as the rest of
     a known whole rather than as a list of numbers. */
  const completedCount = completenessItems.filter(({ data }) => (data ?? 0) >= 1).length

  const partnerId = partnerData[0]?.PartnerId ?? ''

  /* The signature is per offering, and the same record the program-type strip reads its
     `Acknowledged` subtitle from, so the two cannot disagree. */
  const acknowledged = useMemo(
    () =>
      activeOffering
        ? getAcknowledgedOfferingData(completenessData, partnerId, activeOffering)
        : null,
    [completenessData, partnerId, activeOffering]
  )

  /* The date the signature carries, as the program-type strip states it; a signature without one
     still counts as signed, so the chip keeps the dash rather than dropping the fact. */
  const acknowledgedDate = acknowledged?.AcknowledgedDate
    ? (getFormatDate(acknowledged.AcknowledgedDate, 'date3') ?? '--')
    : '--'

  /* The signature strip offers the acts the signature needs from the reader: whoever holds a row
     to sign signs it, required or optional, and a required signature awaited from someone else is
     reminded, whatever that signer's role — so an optional signer gets both. The signers are read
     only while the plan is unsigned, and nothing is offered until they are, so the strip never
     shows the wrong button. */
  const dispatch = useAppDispatch()
  const handleSignersUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])
  const {
    hasLoaded: hasSignersLoaded,
    ownRowToSign,
    requiredSignersToRemind,
    isReminded: isSignerReminded,
    remind: remindSigner,
    isSubmitting: isRemindingSigner,
  } = useAcknowledgmentPendingSigners({
    partnerId,
    fYear,
    offering: !isOverview && !acknowledged ? (activeOffering ?? null) : null,
    onUpdated: handleSignersUpdated,
  })
  const canSign = ownRowToSign !== null
  const signFlow = usePlanSignFlow({ partnerId, fYear, offering: activeOffering ?? null })
  const pbmReminders = usePartnerPbmReminders(
    partnerId,
    activeOffering ?? null,
    ATTENTION_STATUS_BITS.planned
  )

  const isFull = totalPercentage >= 100

  /* A full plan reminds the signers it waits for; a plan still being filled, or one with no such
     signer to name, reminds its PBM to finish it. */
  const remindsSigners = isFull && requiredSignersToRemind.length > 0
  const reminder = remindsSigners
    ? {
        labelKey: NEXT_STEP_KEYS.remind,
        names: requiredSignersToRemind.map(
          (signer) => signer.SignerName?.trim() || signer.SignerEmail || ''
        ),
        isReminded: requiredSignersToRemind.every(isSignerReminded),
        isPending: isRemindingSigner,
        isEmpty: false,
        onRemind: async () => {
          for (const signer of requiredSignersToRemind.filter((row) => !isSignerReminded(row))) {
            await remindSigner(signer)
          }
        },
      }
    : {
        labelKey: isDistributor ? ATTENTION_STATUS_REMIND_DBM_ACTION_KEY : REMIND_PBM_KEY,
        names: pbmReminders.recipients.map(({ name }) => name),
        isReminded: pbmReminders.isReminded,
        isPending: pbmReminders.isPending,
        isEmpty: pbmReminders.recipients.length === 0,
        onRemind: pbmReminders.remindPbm,
      }
  const showsReminder = remindsSigners || !canSign

  /* A full table with no signature is the case the reader would otherwise misread as done. */
  const isFullButUnsigned = isFull && !acknowledged

  /* Why the plan is still unsigned: the signer reads when signing opens, everyone else who signs and
     when. A distributor's plan is signed by its DBM, so its sentences name the DBM. */
  const signReasonKey = canSign
    ? isFull
      ? KEYS.signReady
      : KEYS.signOpensAt
    : isFull
      ? isDistributor
        ? KEYS.signWaitingDistributor
        : KEYS.signWaiting
      : isDistributor
        ? KEYS.signPbmLaterDistributor
        : KEYS.signPbmLater

  /* The cockpit sits on the partner's own page, so a row opens its section right there: the tab
     the section lives on, then the scroll to it. */
  const exitDetail = useAttentionDetailExit()

  const handleOpenSection = useCallback(
    (item: ICompletenessItem) => () => {
      /* A section with a tab of its own opens in its window over the cockpit, so the detail stays
         open under it and is still there once the window is closed. The address carries the tab
         and the request to show it: the sections open their window once the address names the new
         tab, so the window never shows the previous section first. */
      if (item.urlParams && !isRemote) {
        openUrl(appendP360TabScrollIntent(getUrlWithParams(item.urlParams)))
        return
      }

      scrollToElement(item.scrollElementId ?? p360TabsIds)

      if (item.urlParams) {
        openSection(item.urlParams)
        return
      }

      exitDetail()
    },
    [exitDetail, isRemote, openSection, openUrl]
  )

  const signatureText = acknowledged ? (
    <Translation
      span
      text={KEYS.signed}
      replaceParams={[{ key: '{date}', value: acknowledgedDate }]}
    />
  ) : (
    <Translation span text={isDistributor ? KEYS.notSignedDistributor : KEYS.notSigned} />
  )

  /* The section is stated per offering, and the cockpit's filters decide which offerings a partner
     shows: with none of them left there is nothing to measure, and an empty card would read as a
     section that failed to load. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {/* One column, as the overall detail: the plan's own state first, the sections it is made
      of under it. */}
      <div className={styles.body}>
        <div className={styles.planCol}>
          {/* The plan's state in one band: how far it is measured, how much of its table is
          already done and who has signed it. */}
          <div className={styles.plan}>
            <div className={styles.total}>
              <span className={styles.totalValue}>{`${totalPercentage}%`}</span>
              <span className={styles.totalLabel}>
                <Translation span text={KEYS.total} />
              </span>

              {/* The overview has no strip to state the signature in, so the plate keeps it. */}
              {isOverview && (
                <span
                  className={cn(styles.signature, {
                    [styles.signatureSigned]: Boolean(acknowledged),
                    [styles.signatureMissing]: !acknowledged,
                  })}
                >
                  {signatureText}
                </span>
              )}
            </div>

            {/* How much of the table is finished stands with the total rather than over the
            table: the two are the same measurement stated twice, once as a percentage and once
            as a count of sections. */}
            {completenessItems.length > 0 && (
              <span className={styles.planCount}>
                <Translation
                  span
                  text={KEYS.sectionsCount}
                  replaceParams={[
                    { key: '{count}', value: String(completedCount) },
                    { key: '{total}', value: String(completenessItems.length) },
                  ]}
                />
              </span>
            )}

            <span className={styles.totalTrack}>
              <span
                className={styles.totalFill}
                style={{ transform: `scaleX(${totalPercentage / 100})` }}
              />
            </span>
          </div>

          {isOverview && isFullButUnsigned && (
            <p className={styles.warning}>
              <Translation span text={KEYS.notSignedWarning} />
            </p>
          )}
        </div>

        {completenessItems.length === 0 ? (
          <div
            className={cn(styles.sections, styles.sectionsCol)}
            role="img"
            aria-label={getTranslatedText({ t, key: KEYS.notMeasured })}
          >
            {Array.from({ length: EMPTY_SECTION_ROWS }, (_, index) => (
              <span key={index} className={styles.section} />
            ))}
          </div>
        ) : (
          <>
            {/* The widget's own reading of a row — the section's colour filled to its
            percentage, with the name and the number on top of it — in one column, so the names
            read at their full length beside the plan's state. A bar beside the label reads as
            decoration at this size; a bar the label sits on reads as the measurement it is. */}
            <div className={cn(styles.sections, styles.sectionsCol)}>
              {completenessItems.map((item) => (
                /* A bar does not look like a button, so pointing at it says what it does. */
                <Tooltip
                  key={item.id}
                  tipContent={() => <Translation span text={KEYS.openSectionHint} />}
                >
                  <button
                    type="button"
                    className={cn(styles.section, styles.sectionButton)}
                    onClick={handleOpenSection(item)}
                  >
                    <span
                      className={styles.sectionFill}
                      style={{ transform: `scaleX(${item.data ?? 0})`, background: item.color }}
                      aria-hidden
                    />

                    <span className={styles.sectionLabel}>
                      <Translation span text={item.label} />
                    </span>

                    <span
                      className={cn(styles.sectionValue, {
                        [styles.sectionValueShort]: !isOverview && toPercent(item.data) < 100,
                      })}
                    >{`${toPercent(item.data)}%`}</span>
                  </button>
                </Tooltip>
              ))}
            </div>
          </>
        )}
      </div>

      {!isOverview && (
        <>
          {/* The signature in one strip under the lead text: whether the plan is signed, why not
          yet, and the reader's own act on it — so the state is never stated far from the way to
          change it. */}
          <AttentionIntroFoot>
            <div
              className={cn(styles.signStrip, { [styles.signStripSigned]: Boolean(acknowledged) })}
            >
              <span className={styles.signState}>{signatureText}</span>

              {!acknowledged && (
                <span className={styles.signReason}>
                  <Translation span text={signReasonKey} />
                  {!isFull && completenessItems.length > 0 && (
                    <>
                      {' · '}
                      <Translation
                        span
                        text={KEYS.sectionsCount}
                        replaceParams={[
                          { key: '{count}', value: String(completedCount) },
                          { key: '{total}', value: String(completenessItems.length) },
                        ]}
                      />
                    </>
                  )}
                </span>
              )}

              {!acknowledged && hasSignersLoaded && canSign && (
                /* Signing closes the plan, so it takes the success colour; below a full table
                   it wears the library's own disabled look. */
                <Button
                  className={styles.signAct}
                  variant="contained"
                  color="success"
                  size="sm"
                  disabled={!isFull}
                  loading={signFlow.isSigning}
                  onClick={() => signFlow.start(ownRowToSign.EntryId)}
                >
                  <Translation span text={ACKNOWLEDGE_KEY} />
                </Button>
              )}

              {!acknowledged && hasSignersLoaded && showsReminder && (
                /* The reminder is filled in the colour of the missing signature, as the next
                   steps' reminders are. Once sent it stays off for the day, and the tooltip says
                   why. */
                <Tooltip
                  className={styles.signAct}
                  disabled={!reminder.isReminded}
                  tipContent={() => (
                    <Translation
                      span
                      text={REMINDER_KEYS.sentToday}
                      replaceParams={[
                        {
                          key: '{names}',
                          value: reminder.names.join(', '),
                        },
                      ]}
                    />
                  )}
                >
                  <span className={styles.signActTrigger}>
                    <Button
                      variant="contained"
                      color="warning"
                      size="sm"
                      icon={cn('kbase', reminder.isReminded ? 'kbase-check' : 'kbase-bell')}
                      disabled={reminder.isEmpty || reminder.isReminded || reminder.isPending}
                      onClick={() => void reminder.onRemind()}
                    >
                      <Translation span text={reminder.labelKey} />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          </AttentionIntroFoot>

          {/* Signing and reminding stand in the strip and every section row opens its section, so
          the bar drops them and keeps the rest of what the reader's role may do. The offering is
          not switched off from here. */}
          <AttentionStatusActions
            bitValue={ATTENTION_STATUS_BITS.planned}
            className={styles.actions}
            hidden={['acknowledge', 'switchOffOffering', 'remindPbm', 'goToSection']}
          />

          {signFlow.element}
        </>
      )}
    </div>
  )
}

AttentionStatusCompleteness.displayName = 'AttentionStatusCompleteness'
